import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveSessionIdSafe } from '../../../contexts/SessionContext';
import { apiService } from '../../../services/ApiService';
import { pickExtraction, rangeText } from './VisionDebugInline';

/**
 * "What the photo showed" — opened from the info icon on the marked photo, for
 * every user. Each part is shown as the four indications of the Image analysis
 * review (2026-10-03):
 *
 * - Identity — catalog name, condition, features the part carries
 * - Geometry — form, profile, cross-section, ends, feature counts, handedness
 * - Dimensions — length / width / height as ranges, with the scale reference
 * - Adjacency map — visible anchor parts around it and how they relate
 *
 * A whole indication the analysis did not produce says so in words; a single
 * field the photo cannot show (or that does not apply — a panel has no ends)
 * is a dash. Neither disappears: an empty indication is information too.
 *
 * Reads GET /api/chat/vision-summary (customer-safe fields of the user's own
 * session).
 */

type Dict = Record<string, any>;

const plain = (v: unknown) => String(v ?? '').replace(/_/g, ' ').trim();

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex gap-2 text-[12px] leading-snug">
    <span className="shrink-0 w-28 text-gray-500">{label}</span>
    <span className="min-w-0 text-gray-800 break-words">{children}</span>
  </div>
);

const Section: React.FC<{ title: string; hint: string; testId: string; children: React.ReactNode }> = ({ title, hint, testId, children }) => (
  <div data-testid={testId} className="rounded-lg border border-gray-200 bg-white px-2.5 py-2 space-y-1">
    <div className="flex items-baseline gap-2">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-700">{title}</span>
      <span className="text-[10.5px] text-gray-400">{hint}</span>
    </div>
    {children}
  </div>
);

const VisionPhotoDetails: React.FC<{ messageTime?: unknown }> = ({ messageTime }) => {
  const { t } = useTranslation();
  const sessionId = useActiveSessionIdSafe();
  const [extractions, setExtractions] = useState<Dict[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    let alive = true;
    apiService.get(`/api/chat/vision-summary?session_id=${encodeURIComponent(sessionId)}`)
      .then(res => { if (alive) setExtractions(res.data?.extractions || []); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [sessionId]);

  if (failed) {
    return <div className="text-[12px] text-gray-500">{t('chat.visionDetails.unavailable', { defaultValue: 'The photo details are not available right now.' })}</div>;
  }
  if (!extractions) {
    return <div className="text-[12px] text-gray-400">{t('chat.visionDetails.loading', { defaultValue: 'Loading…' })}</div>;
  }
  const items: Dict[] = pickExtraction(extractions, messageTime)?.cleaned?.identified_items || [];
  if (!items.length) {
    return <div className="text-[12px] text-gray-500">{t('chat.visionDetails.none', { defaultValue: 'No parts were identified in this photo.' })}</div>;
  }
  // Two different empties: a whole indication the analysis did not produce
  // (said once, in words), and one field the photo cannot show or that does
  // not apply to this part — a panel has no "ends" — shown as a dash.
  const notProduced = t('chat.visionDetails.notProduced', {
    defaultValue: 'Not produced by this analysis.',
  }) as string;
  const notApplicable = t('chat.visionDetails.notApplicable', {
    defaultValue: 'Not visible in the photo, or does not apply to this part',
  }) as string;
  const or = (v: string) => v || <span className="text-gray-400" title={notApplicable}>—</span>;
  const absent = <div className="text-[12px] text-gray-400" data-testid="indication-absent">{notProduced}</div>;

  return (
    <div data-testid="vision-photo-details" className="space-y-4">
      {items.map((it, i) => {
        const hasGeometry = !!(it.geometry && typeof it.geometry === 'object');
        const g: Dict = hasGeometry ? it.geometry : {};
        const hasDims = !!(g.dimensions && typeof g.dimensions === 'object');
        const d: Dict = hasDims ? g.dimensions : {};
        const anchors: Dict[] = Array.isArray(it.adjacency) ? it.adjacency : [];
        const carried = anchors.filter(a => a.relation === 'carries').map(a => a.name);
        const aliases: string[] = Array.isArray(it.catalog_aliases) ? it.catalog_aliases : [];
        const counts = Array.isArray(g.surface_features)
          ? g.surface_features.map((f: Dict) => `${f.partial ? '≥ ' : ''}${f.count} ${plain(f.kind)}`).join(', ') : '';
        const ends = Array.isArray(g.ends) ? g.ends.map((e: Dict) => `${plain(e.end)}: ${plain(e.kind)}`).join(', ') : '';
        const handed = g.handed == null ? ''
          : g.handed ? `${t('chat.visionDetails.handed', { defaultValue: 'left/right version' })}${g.handed_reason ? ` — ${g.handed_reason}` : ''}`
            : t('chat.visionDetails.symmetric', { defaultValue: 'symmetric' }) as string;
        const profile = g.profile ? `${plain(g.profile)}${g.bend_count != null ? ` (${g.bend_count} ${t('chat.visionDetails.bends', { defaultValue: 'bends' })})` : ''}` : '';
        const scale = [d.scale_reference, typeof d.confidence === 'number' ? `${Math.round(d.confidence * 100)}%` : '',
          d.angled_shot ? t('chat.visionDetails.angled', { defaultValue: 'angled shot' }) : ''].filter(Boolean).join(' · ');
        return (
          <div key={i} className="space-y-1.5">
            <div className="text-[13px] font-semibold text-gray-900">
              <span className="mr-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-gray-800 text-[10px] text-white align-middle">{i + 1}</span>
              {it.part_name}
              {typeof it.confidence_score === 'number' && (
                <span className="ml-2 text-[11px] font-medium text-gray-500">{Math.round(it.confidence_score * 100)}%</span>
              )}
            </div>
            {it.detailed_description && <div className="text-[12px] text-gray-600">{it.detailed_description}</div>}

            <Section testId="indication-identity" title={t('chat.visionDetails.identity', { defaultValue: 'Identity' }) as string}
              hint={t('chat.visionDetails.identityHint', { defaultValue: 'picks the section and part family' }) as string}>
              <Row label={t('chat.visionDetails.catalogName', { defaultValue: 'Catalog name' })}>{or(aliases.join(' · '))}</Row>
              <Row label={t('chat.visionDetails.condition', { defaultValue: 'Condition' })}>
                {or([it.condition, it.damage_type !== it.condition && it.damage_type, it.damage_severity].filter(Boolean).map(plain).join(' · '))}
              </Row>
              <Row label={t('chat.visionDetails.carries', { defaultValue: 'Carries' })}>{or(carried.join(', '))}</Row>
              {Array.isArray(it.missing_evidence) && it.missing_evidence.length > 0 && (
                <Row label={t('chat.visionDetails.missingBecause', { defaultValue: 'Missing because' })}>{it.missing_evidence.map(plain).join(', ')}</Row>
              )}
            </Section>

            <Section testId="indication-geometry" title={t('chat.visionDetails.geometry', { defaultValue: 'Geometry' }) as string}
              hint={t('chat.visionDetails.geometryHint', { defaultValue: 'matched against the catalog drawing' }) as string}>
              {!hasGeometry ? absent : (<>
              <Row label={t('chat.visionDetails.form', { defaultValue: 'Form' })}>{or(plain(g.overall_form))}</Row>
              <Row label={t('chat.visionDetails.profile', { defaultValue: 'Profile' })}>{or(profile)}</Row>
              <Row label={t('chat.visionDetails.crossSection', { defaultValue: 'Cross-section' })}>{or(plain(g.cross_section))}</Row>
              <Row label={t('chat.visionDetails.ends', { defaultValue: 'Ends' })}>{or(ends)}</Row>
              <Row label={t('chat.visionDetails.featureCounts', { defaultValue: 'Feature counts' })}>{or(counts)}</Row>
              <Row label={t('chat.visionDetails.handedness', { defaultValue: 'Handedness' })}>{or(handed)}</Row>
              {g.orientation && <Row label={t('chat.visionDetails.orientation', { defaultValue: 'Orientation' })}>{plain(g.orientation)}</Row>}
              </>)}
            </Section>

            <Section testId="indication-dimensions" title={t('chat.visionDetails.dimensions', { defaultValue: 'Dimensions' }) as string}
              hint={t('chat.visionDetails.dimensionsHint', { defaultValue: 'full part vs section, size variants' }) as string}>
              {!hasDims ? absent : (<>
              <Row label={t('chat.visionDetails.length', { defaultValue: 'Length' })}>{or(rangeText(d.length))}</Row>
              <Row label={t('chat.visionDetails.width', { defaultValue: 'Width' })}>{or(rangeText(d.width))}</Row>
              <Row label={t('chat.visionDetails.height', { defaultValue: 'Height' })}>{or(rangeText(d.height))}</Row>
              <Row label={t('chat.visionDetails.ratio', { defaultValue: 'Width : height' })}>{or(d.width_to_height_ratio != null ? `${d.width_to_height_ratio} : 1` : '')}</Row>
              <Row label={t('chat.visionDetails.scale', { defaultValue: 'Measured against' })}>{or(scale)}</Row>
              </>)}
            </Section>

            <Section testId="indication-adjacency" title={t('chat.visionDetails.adjacency', { defaultValue: 'Adjacency map' }) as string}
              hint={t('chat.visionDetails.adjacencyHint', { defaultValue: 'finds the section and callout' }) as string}>
              {anchors.length ? anchors.map((a, j) => (
                <Row key={j} label={plain(a.relation === 'position' && a.direction ? a.direction : a.relation)}>{a.name}</Row>
              )) : absent}
            </Section>
          </div>
        );
      })}
    </div>
  );
};

export default VisionPhotoDetails;
